import axios from 'axios';
import { attachAuthInterceptor } from './authInterceptor';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Challan print-variance API.
 * ADMIN / SUPER_ADMIN only.
 *
 * Reports challans GENERATED on a given day that were not PRINTED on that same day —
 * including the ones never printed at all. The date filter is always the generated
 * date, so a challan generated on the 28th and printed on the 29th belongs to the 28th.
 *
 * Base: `${API_BASE_URL}/api/v1/challan-print-variance`
 */
const varianceAPI = axios.create({
    baseURL: `${API_BASE_URL}/api/v1/challan-print-variance`,
    headers: { 'Content-Type': 'application/json' },
    paramsSerializer: warehouseParamsSerializer,
});

// Session-expiry handling (401 -> clear + redirect), consistent with feature services.
attachAuthInterceptor(varianceAPI);

varianceAPI.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return applyWarehouseFilter(config);
    },
    (error) => Promise.reject(error)
);

const challanPrintVarianceService = {
    /** Per-day counts of same-day-print misses across the picked range. */
    async getSummary(params = {}, options = {}) {
        try {
            const response = await varianceAPI.get('/summary', { params, ...options });
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) {
                return { success: false, error: 'Request canceled', isCanceled: true };
            }
            return { success: false, error: extractApiError(error) };
        }
    },

    /** The individual challans behind the summary, paginated. */
    async getList(params = {}, options = {}) {
        try {
            const response = await varianceAPI.get('/list', { params, ...options });
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) {
                return { success: false, error: 'Request canceled', isCanceled: true };
            }
            return { success: false, error: extractApiError(error) };
        }
    },
};

export default challanPrintVarianceService;
